import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import ExpandableSection from "@/components/job/ExpandableSection";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MultiSelect } from "@/components/ui/multi-select";
import { additionalNotesQueryOptions } from "@/hooks/useAdditionalNotes";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";

import { NoteComponent, type NoteSchema } from "./NoteComponent";

const cardName = "drawingNotes";

export function DrawingNotes() {
  const { sections, toggleExpandableSection, setDrawingNotes } = useCUMechanicalJobStore();
  const savedDrawingNotes = sections.drawingNotes;

  const { data: { mappedNotesOptions = [] } = {} } = useQuery({
    ...additionalNotesQueryOptions("mechanical"),
    select: (data) => ({
      mappedNotesOptions: data?.additional_notes?.map((note) => ({ label: note.name, value: String(note.id) })) ?? [],
    }),
  });

  const [additional_notes, setAdditional_notes] = useState<Array<string>>(
    savedDrawingNotes.data.additional_notes?.map(String) ?? []
  );
  const [noteComponents, setNoteComponents] = useState<NoteSchema[]>(
    savedDrawingNotes.data.new_notes?.length
      ? savedDrawingNotes.data.new_notes.map((note) => ({ ...note, id: note.id ?? crypto.randomUUID() }))
      : [{ id: crypto.randomUUID() }]
  );

  useEffect(() => {
    // If editing a draft, load saved selections
    if (savedDrawingNotes.isDataFilled) {
      setAdditional_notes(savedDrawingNotes.data.additional_notes?.map(String) ?? []);
      setNoteComponents(
        savedDrawingNotes.data.new_notes?.length
          ? savedDrawingNotes.data.new_notes.map((note) => ({ ...note, id: note.id ?? crypto.randomUUID() }))
          : [{ id: crypto.randomUUID() }]
      );
    }
  }, [savedDrawingNotes.data.additional_notes, savedDrawingNotes.data.new_notes, savedDrawingNotes.isDataFilled]);

  function handleAddNote() {
    setNoteComponents([...noteComponents, { id: crypto.randomUUID(), custom_name_of_note: "", custom_note: "" }]);
  }

  function handleNoteChange(index: number, data: NoteSchema) {
    const updatedComponents = [...noteComponents];
    updatedComponents[index] = data;
    setNoteComponents(updatedComponents);
  }

  function handleNoteDelete(index: number) {
    const updatedComponents = [...noteComponents];
    updatedComponents.splice(index, 1);
    setNoteComponents(updatedComponents);
  }

  function handleSaveDrawingNotes() {
    const customNotes = noteComponents.filter((n) => n.custom_name_of_note || n.custom_note);

    setDrawingNotes({
      data: {
        additional_notes: additional_notes.map((note) => Number(note)).filter((n) => !Number.isNaN(n)),
        new_notes: customNotes,
      },
      status: "done",
    });
  }

  return (
    <ExpandableSection
      setIsExpanded={() => toggleExpandableSection(cardName)}
      status={savedDrawingNotes.status}
      title="Drawing Notes"
    >
      <Label>Additional notes</Label>
      <MultiSelect
        value={additional_notes}
        defaultValue={additional_notes}
        options={mappedNotesOptions}
        onValueChange={(selected) => setAdditional_notes(selected)}
      />

      {noteComponents.map((note, index) => (
        <NoteComponent
          key={note.id}
          index={index}
          data={note}
          onChange={handleNoteChange}
          onDelete={handleNoteDelete}
        />
      ))}

      <div className="flex justify-end gap-2">
        <Button variant="outline" className="mt-6" onClick={handleAddNote}>
          Add Note
        </Button>
        <Button className="mt-6" onClick={handleSaveDrawingNotes}>
          Save
        </Button>
      </div>
    </ExpandableSection>
  );
}
